"use client";

import { useState, useCallback, useRef } from "react";
import { useLinkAccount, usePrivy } from "@privy-io/react-auth";
import { privyAuthService } from "@/services/privy-auth.service";

export interface MergeConflictData {
	sourceUserId: string;
	targetUserId: string;
	sourceProfile: { userName?: string; drops: number; snags: number };
	targetProfile: { userName?: string; drops: number; snags: number };
}

export type LinkConflictType = "email" | "phone" | "twitter" | "google" | "discord" | "wallet" | null;

export interface UseAccountLinkingReturn {
	// Forces a fresh backend sync (privy-login) using the current Privy user.
	// Call this after any out-of-band data change (e.g. merge-accounts) so the
	// JWT cookie and in-memory state reflect the latest MongoDB state.
	reSync: () => Promise<void>;

	// Link methods (Privy-side + backend re-sync on success)
	linkEmail: () => void;
	linkPhone: () => void;
	linkTwitter: () => void;
	linkGoogle: () => void;
	linkDiscord: () => void;
	linkPhantom: () => Promise<void>;

	// Pre-check an email for duplicates before opening Privy's link modal.
	// Returns "duplicate" if another MGT account already has this email
	// (mergeConflict will be populated for the MergeConfirmationModal),
	// "ok" if the email is free to link (Privy modal is opened), or "error"
	// on lookup failure. Avoids the "OTP loop" reported in Task 8.
	linkEmailWithPreCheck: (email: string) => Promise<"duplicate" | "ok" | "error">;

	// Same idea for phone numbers — surfaces the merge prompt before Privy's
	// modal opens so a user signing in on PC who tries to link their existing
	// mobile phone is offered the merge UI immediately (Task 10).
	linkPhoneWithPreCheck: (phone: string) => Promise<"duplicate" | "ok" | "error">;

	// Unlink methods (Privy-side + backend re-sync)
	unlinkEmail: (email: string) => Promise<void>;
	unlinkPhone: (phone: string) => Promise<void>;
	unlinkTwitter: (subject: string) => Promise<void>;
	unlinkGoogle: (subject: string) => Promise<void>;
	unlinkDiscord: (subject: string) => Promise<void>;
	unlinkPhantom: (address: string) => Promise<void>;

	// Sync / error state
	syncError: string | null;
	clearSyncError: () => void;
	isSyncing: boolean;

	// Which link type just failed with linked_to_another_user (null = no conflict)
	linkConflictType: LinkConflictType;

	// Merge conflict (set when a pre-check detects a duplicate account)
	mergeConflict: MergeConflictData | null;
	clearMergeConflict: () => void;

	// Looks up an email address in MongoDB to find the conflicting account,
	// then populates mergeConflict so the MergeConfirmationModal can be shown.
	lookupEmailForMerge: (email: string) => Promise<"found" | "not_found" | "error">;

	// Looks up a phone number in MongoDB to find the conflicting account,
	// then populates mergeConflict so the MergeConfirmationModal can be shown.
	// Used when linking a phone on PC fails because the number is already
	// on the user's mobile-first account.
	lookupPhoneForMerge: (phone: string) => Promise<"found" | "not_found" | "error">;
}

export function useAccountLinking(): UseAccountLinkingReturn {
	const { user: privyUser } = usePrivy();

	// Unlink methods live on usePrivy()
	const {
		unlinkEmail: privyUnlinkEmail,
		unlinkPhone: privyUnlinkPhone,
		unlinkTwitter: privyUnlinkTwitter,
		unlinkGoogle: privyUnlinkGoogle,
		unlinkDiscord: privyUnlinkDiscord,
		unlinkWallet: privyUnlinkWallet,
	} = usePrivy() as any;

	const [syncError, setSyncError] = useState<string | null>(null);
	const [isSyncing, setIsSyncing] = useState(false);
	const [mergeConflict, setMergeConflict] = useState<MergeConflictData | null>(null);
	// Exposed: which link type just failed with linked_to_another_user
	const [linkConflictType, setLinkConflictType] = useState<LinkConflictType>(null);
	// Internal ref: which link type is currently in-flight (doesn't need to re-render)
	const pendingLinkTypeRef = useRef<LinkConflictType>(null);
	const pendingWalletAddressRef = useRef<string | null>(null);

	const clearSyncError = useCallback(() => {
		setSyncError(null);
		setLinkConflictType(null);
	}, []);
	const clearMergeConflict = useCallback(() => setMergeConflict(null), []);

	/**
	 * Converts a raw Privy error into a user-readable message using a
	 * default-deny posture: only return an actionable banner string when the
	 * error matches an explicit allowlist; everything else (modal closed,
	 * OAuth dismissed, generic SDK errors after a back-button) resolves to
	 * "cancelled" so the Profile screen does not bleed raw error text after
	 * the user exits the linking menu (Task 9).
	 */
	const privyErrorMessage = useCallback((err: any): string => {
		const msg: string = err?.message ?? err?.error ?? String(err ?? "");
		const lower = msg.toLowerCase();

		// Allowlist of actionable errors — only these surface a banner.
		if (
			msg.includes("linked_to_another_user") ||
			lower.includes("already been linked") ||
			lower.includes("already linked to another")
		) {
			return "This account is already connected to a different profile. If it's yours, use Merge Accounts to combine them.";
		}
		if (lower.includes("cannot unlink") || lower.includes("must have at least")) {
			return "You need at least one other linked account before unlinking this one.";
		}
		if (
			lower.includes("invalid_otp") ||
			lower.includes("invalid_code") ||
			lower.includes("invalid otp") ||
			lower.includes("invalid code") ||
			lower.includes("incorrect code") ||
			lower.includes("incorrect_code")
		) {
			return "That code didn't match. Request a new one and try again.";
		}
		if (
			lower.includes("expired_otp") ||
			lower.includes("expired_code") ||
			lower.includes("code expired") ||
			lower.includes("otp expired")
		) {
			return "This code expired. Request a new one and try again.";
		}
		if (lower.includes("rate_limit") || lower.includes("too many")) {
			return "Too many attempts. Please wait a moment and try again.";
		}
		if (lower.includes("network") || lower.includes("fetch failed") || lower.includes("timeout")) {
			return "Network issue. Check your connection and try again.";
		}

		// Everything else — including modal close, OAuth dismiss, back-button,
		// generic invalid_credentials handshakes, "user cancelled / denied",
		// "popup closed", "exited_auth_flow", etc — is treated as cancellation
		// so we do not show a spurious banner.
		return "cancelled";
	}, []);

	/** After any Privy-side link/unlink, re-sync with the backend. */
	const afterChange = useCallback(
		async (updatedPrivyUser?: any) => {
			const userToSync = updatedPrivyUser ?? privyUser;
			if (!userToSync) return;
			setIsSyncing(true);
			const result = await privyAuthService.syncWithRetry(userToSync);
			setIsSyncing(false);
			if (result.mergeConflict) {
				setMergeConflict(result.mergeConflict);
			}
			if (!result.success && result.error) {
				setSyncError(result.error);
			} else {
				setSyncError(null);
			}
		},
		[privyUser]
	);

	const lookupWalletForMerge = useCallback(
		async (address: string): Promise<"found" | "not_found" | "error"> => {
			try {
				const currentUserId = privyUser?.id
					? await resolveCurrentMongoId(privyUser.id)
					: null;

				const params = new URLSearchParams({ address });
				if (currentUserId) params.set("currentUserId", currentUserId);

				const res = await fetch(
					`/api/user/auth/wallet-merge-check?${params.toString()}`
				);
				if (!res.ok) return "error";

				const data = await res.json();
				if (!data.conflict || !data.sourceUserId) return "not_found";

				setMergeConflict({
					sourceUserId: data.sourceUserId,
					targetUserId: currentUserId ?? "",
					sourceProfile: data.sourceProfile,
					targetProfile: {
						...data.targetProfile,
						userName:
							privyUser?.google?.name ?? privyUser?.email?.address ?? undefined,
					},
				});
				return "found";
			} catch {
				return "error";
			}
		},
		[privyUser]
	);

	// Single useLinkAccount call — destructure all link methods from it
	const {
		linkEmail: privyLinkEmail,
		linkPhone: privyLinkPhone,
		linkTwitter: privyLinkTwitter,
		linkGoogle: privyLinkGoogle,
		linkDiscord: privyLinkDiscord,
		linkWallet: privyLinkWallet,
	} = useLinkAccount({
		onSuccess: ({ user }) => {
			pendingLinkTypeRef.current = null;
			pendingWalletAddressRef.current = null;
			afterChange(user);
		},
		onError: (err) => {
			const friendly = privyErrorMessage(err);
			// PrivyErrorCode is a string literal — the code itself IS the message
			const msg: string = String(err ?? "");
			const isAlreadyLinked =
				msg.includes("linked_to_another_user") ||
				msg.toLowerCase().includes("already been linked") ||
				msg.toLowerCase().includes("already linked to another");

			// Structured diagnostic log — keeps OTP-rejected and other unusual
			// failures visible without leaking raw error text to the UI banner
			// (Task 8). `friendly === "cancelled"` is the default-deny bucket
			// and is intentionally noisy only in local/dev.
			if (friendly !== "cancelled" || process.env.NEXT_PUBLIC_ENV === "local") {
				console.warn("[useAccountLinking] link error", {
					linkType: pendingLinkTypeRef.current,
					rawMessage: msg,
					friendly,
				});
			}

			if (isAlreadyLinked) {
				// Record which link type caused the conflict so the UI can offer merge
				setLinkConflictType(pendingLinkTypeRef.current);
				if (pendingLinkTypeRef.current === "wallet") {
					const walletAddress =
						pendingWalletAddressRef.current ??
						(typeof window !== "undefined"
							? (window as any).solana?.publicKey?.toString()
							: null);
					if (walletAddress) {
						void lookupWalletForMerge(walletAddress);
					}
				}
			}

			pendingLinkTypeRef.current = null;
			pendingWalletAddressRef.current = null;

			if (friendly !== "cancelled") {
				setSyncError(friendly);
			}
		},
	});

	// ── Exposed link functions ────────────────────────────────────────────────

	const linkEmail = useCallback(() => {
		pendingLinkTypeRef.current = "email";
		privyLinkEmail();
	}, [privyLinkEmail]);

	/**
	 * Pre-check an email for duplicate ownership BEFORE opening Privy's link
	 * modal. If the email already belongs to another MGT user we surface the
	 * MergeConfirmationModal directly; otherwise we fall through to the normal
	 * Privy email-link flow. This avoids the "OTP loop" reported in Task 8
	 * where a duplicate email made Privy keep sending OTPs that could never
	 * complete linking.
	 */
	const linkEmailWithPreCheck = useCallback(
		async (email: string): Promise<"duplicate" | "ok" | "error"> => {
			const trimmed = email.trim().toLowerCase();
			if (!trimmed) return "error";

			try {
				const currentUserId = privyUser?.id
					? await resolveCurrentMongoId(privyUser.id)
					: null;

				const params = new URLSearchParams({ email: trimmed });
				if (currentUserId) params.set("currentUserId", currentUserId);

				const res = await fetch(
					`/api/user/auth/email-merge-check?${params.toString()}`
				);
				if (!res.ok) return "error";
				const data = await res.json();

				if (data?.found) {
					setMergeConflict({
						sourceUserId: data.sourceUserId,
						targetUserId: currentUserId ?? "",
						sourceProfile: data.sourceProfile,
						targetProfile: {
							...data.targetProfile,
							userName:
								privyUser?.google?.name ?? privyUser?.email?.address ?? undefined,
						},
					});
					return "duplicate";
				}

				pendingLinkTypeRef.current = "email";
				privyLinkEmail();
				return "ok";
			} catch {
				return "error";
			}
		},
		[privyUser, privyLinkEmail]
	);

	const linkPhone = useCallback(() => {
		pendingLinkTypeRef.current = "phone";
		privyLinkPhone();
	}, [privyLinkPhone]);

	/**
	 * Pre-check a phone number for duplicate ownership BEFORE opening Privy's
	 * link modal — same pattern as `linkEmailWithPreCheck`. The common
	 * scenario: user created a mobile account with phone, then signs into PC
	 * with Phantom and tries to link that same phone (Task 10). Surfacing the
	 * merge UI here avoids the SDK's opaque error path entirely.
	 */
	const linkPhoneWithPreCheck = useCallback(
		async (phone: string): Promise<"duplicate" | "ok" | "error"> => {
			const trimmed = phone.trim();
			if (!trimmed) return "error";

			try {
				const currentUserId = privyUser?.id
					? await resolveCurrentMongoId(privyUser.id)
					: null;

				const params = new URLSearchParams({ phone: trimmed });
				if (currentUserId) params.set("currentUserId", currentUserId);

				const res = await fetch(
					`/api/user/auth/phone-merge-check?${params.toString()}`
				);
				if (!res.ok) return "error";
				const data = await res.json();

				if (data?.found) {
					setMergeConflict({
						sourceUserId: data.sourceUserId,
						targetUserId: currentUserId ?? "",
						sourceProfile: data.sourceProfile,
						targetProfile: {
							...data.targetProfile,
							userName:
								privyUser?.google?.name ?? privyUser?.email?.address ?? undefined,
						},
					});
					return "duplicate";
				}

				pendingLinkTypeRef.current = "phone";
				privyLinkPhone();
				return "ok";
			} catch {
				return "error";
			}
		},
		[privyUser, privyLinkPhone]
	);

	const linkTwitter = useCallback(() => {
		pendingLinkTypeRef.current = "twitter";
		privyLinkTwitter();
	}, [privyLinkTwitter]);

	const linkGoogle = useCallback(() => {
		pendingLinkTypeRef.current = "google";
		privyLinkGoogle();
	}, [privyLinkGoogle]);

	const linkDiscord = useCallback(() => {
		pendingLinkTypeRef.current = "discord";
		privyLinkDiscord();
	}, [privyLinkDiscord]);

	/** Links Phantom wallet through Privy so dashboard account transfer can run. */
	const linkPhantom = useCallback(async () => {
		const phantomAddress: string | undefined =
			typeof window !== "undefined"
				? (window as any).solana?.publicKey?.toString()
				: undefined;

		pendingLinkTypeRef.current = "wallet";
		pendingWalletAddressRef.current = phantomAddress ?? null;
		privyLinkWallet({ walletList: ["phantom"] as const });
	}, [privyLinkWallet]);

	// ── Exposed unlink functions ─────────────────────────────────────────────

	const unlinkEmail = useCallback(
		async (email: string) => {
			try {
				await privyUnlinkEmail(email);
				await afterChange();
			} catch (err) {
				const msg = privyErrorMessage(err);
				if (msg !== "cancelled") setSyncError(msg);
			}
		},
		[privyUnlinkEmail, afterChange, privyErrorMessage]
	);

	const unlinkPhone = useCallback(
		async (phone: string) => {
			try {
				await privyUnlinkPhone(phone);
				await afterChange();
			} catch (err) {
				const msg = privyErrorMessage(err);
				if (msg !== "cancelled") setSyncError(msg);
			}
		},
		[privyUnlinkPhone, afterChange, privyErrorMessage]
	);

	const unlinkTwitter = useCallback(
		async (subject: string) => {
			try {
				await privyUnlinkTwitter(subject);
				await afterChange();
			} catch (err) {
				const msg = privyErrorMessage(err);
				if (msg !== "cancelled") setSyncError(msg);
			}
		},
		[privyUnlinkTwitter, afterChange, privyErrorMessage]
	);

	const unlinkGoogle = useCallback(
		async (subject: string) => {
			try {
				await privyUnlinkGoogle(subject);
				await afterChange();
			} catch (err) {
				const msg = privyErrorMessage(err);
				if (msg !== "cancelled") setSyncError(msg);
			}
		},
		[privyUnlinkGoogle, afterChange, privyErrorMessage]
	);

	const unlinkDiscord = useCallback(
		async (subject: string) => {
			try {
				await privyUnlinkDiscord(subject);
				await afterChange();
			} catch (err) {
				const msg = privyErrorMessage(err);
				if (msg !== "cancelled") setSyncError(msg);
			}
		},
		[privyUnlinkDiscord, afterChange, privyErrorMessage]
	);

	const unlinkPhantom = useCallback(
		async (address: string) => {
			try {
				await privyUnlinkWallet(address);
				await afterChange();
			} catch (err) {
				const msg = privyErrorMessage(err);
				if (msg !== "cancelled") setSyncError(msg);
			}
		},
		[privyUnlinkWallet, afterChange, privyErrorMessage]
	);

	/**
	 * Looks up an email address in MongoDB to find the conflicting account,
	 * then populates mergeConflict so the MergeConfirmationModal can be shown.
	 *
	 * Used when linking an email fails with linked_to_another_user — the user
	 * enters the email they tried to link and we surface the merge flow.
	 */
	const lookupEmailForMerge = useCallback(
		async (email: string): Promise<"found" | "not_found" | "error"> => {
			try {
				const currentUserId = privyUser?.id
					? await resolveCurrentMongoId(privyUser.id)
					: null;

				const params = new URLSearchParams({ email });
				if (currentUserId) params.set("currentUserId", currentUserId);

				const res = await fetch(
					`/api/user/auth/email-merge-check?${params.toString()}`
				);
				if (!res.ok) return "error";

				const data = await res.json();
				if (!data.found) return "not_found";

				setMergeConflict({
					sourceUserId: data.sourceUserId,
					targetUserId: currentUserId ?? "",
					sourceProfile: data.sourceProfile,
					targetProfile: {
						...data.targetProfile,
						userName: privyUser?.google?.name ?? privyUser?.email?.address ?? undefined,
					},
				});
				return "found";
			} catch {
				return "error";
			}
		},
		[privyUser]
	);

	/**
	 * Looks up a phone number in MongoDB to find the conflicting account,
	 * then populates mergeConflict so the MergeConfirmationModal can be shown.
	 *
	 * Used when linking a phone fails with linked_to_another_user — common
	 * case: user created a mobile account with phone, then signs into PC
	 * with a wallet and tries to link the same phone.
	 */
	const lookupPhoneForMerge = useCallback(
		async (phone: string): Promise<"found" | "not_found" | "error"> => {
			try {
				const currentUserId = privyUser?.id
					? await resolveCurrentMongoId(privyUser.id)
					: null;

				const params = new URLSearchParams({ phone });
				if (currentUserId) params.set("currentUserId", currentUserId);

				const res = await fetch(
					`/api/user/auth/phone-merge-check?${params.toString()}`
				);
				if (!res.ok) return "error";

				const data = await res.json();
				if (!data.found) return "not_found";

				setMergeConflict({
					sourceUserId: data.sourceUserId,
					targetUserId: currentUserId ?? "",
					sourceProfile: data.sourceProfile,
					targetProfile: {
						...data.targetProfile,
						userName:
							privyUser?.google?.name ?? privyUser?.email?.address ?? undefined,
					},
				});
				return "found";
			} catch {
				return "error";
			}
		},
		[privyUser]
	);

	const reSync = useCallback(async () => {
		await afterChange();
	}, [afterChange]);

	return {
		reSync,
		linkEmail,
		linkEmailWithPreCheck,
		linkPhone,
		linkPhoneWithPreCheck,
		linkTwitter,
		linkGoogle,
		linkDiscord,
		linkPhantom,
		unlinkEmail,
		unlinkPhone,
		unlinkTwitter,
		unlinkGoogle,
		unlinkDiscord,
		unlinkPhantom,
		syncError,
		clearSyncError,
		isSyncing,
		linkConflictType,
		mergeConflict,
		clearMergeConflict,
		lookupEmailForMerge,
		lookupPhoneForMerge,
	};
}

/**
 * Resolves a Privy user ID to the MongoDB _id for the conflict check.
 * Reads from the auth cookie (same source the app uses for currentUser).
 */
async function resolveCurrentMongoId(privyId: string): Promise<string | null> {
	try {
		const { COOKIE_NAME } = await import("@/constants/middleware.constants");
		const Cookies = (await import("js-cookie")).default;
		const raw = Cookies.get(COOKIE_NAME);
		if (!raw) return null;
		const user = JSON.parse(raw);
		if (user?.privyId === privyId && user?.id) return user.id;
		return null;
	} catch {
		return null;
	}
}
